import React from 'react';

/**
 * MCG Logo component
 * variant: 'dark' (navy text for light backgrounds) | 'light' (gold text for dark backgrounds)
 * size: 'sm' | 'md' | 'lg' | 'xl'
 * showTagline: whether to show the subtitle tagline
 */
const Logo = ({ variant = 'dark', size = 'md', showTagline = false, className = '' }) => {
  const isDark = variant === 'dark';

  const sizes = {
    sm: {
      monogram: 'text-xl md:text-2xl',
      name: 'text-[6px] md:text-[7px]',
      tagline: 'text-[5px] md:text-[6px]',
      lineWidth: 'w-6 md:w-8',
      gap: 'gap-0.5',
      dotSize: 'w-1 h-1',
      wrapper: 'py-1',
    },
    md: {
      monogram: 'text-2xl md:text-3xl',
      name: 'text-[7px] md:text-[8px]',
      tagline: 'text-[6px] md:text-[7px]',
      lineWidth: 'w-8 md:w-12',
      gap: 'gap-1',
      dotSize: 'w-1 h-1',
      wrapper: 'py-1',
    },
    lg: {
      monogram: 'text-4xl md:text-5xl',
      name: 'text-[9px] md:text-[11px]',
      tagline: 'text-[7px] md:text-[9px]',
      lineWidth: 'w-12 md:w-20',
      gap: 'gap-1',
      dotSize: 'w-1.5 h-1.5',
      wrapper: 'py-2',
    },
    xl: {
      monogram: 'text-5xl md:text-7xl',
      name: 'text-[10px] md:text-[13px]',
      tagline: 'text-[8px] md:text-[10px]',
      lineWidth: 'w-16 md:w-24',
      gap: 'gap-1.5',
      dotSize: 'w-1.5 h-1.5',
      wrapper: 'py-3',
    },
  };

  const s = sizes[size];

  const primaryColor = isDark ? 'text-navy-900' : 'text-gold-400';
  const secondaryColor = isDark ? 'text-navy-900/70' : 'text-cream-200';
  const accentColor = 'text-gold-400';
  const lineColor = isDark ? 'bg-gold-500' : 'bg-gold-400';

  return (
    <div className={`flex flex-col items-center ${s.wrapper} ${className}`}>
      {/* Top gold line */}
      <div className={`${s.lineWidth} h-px ${lineColor} mb-1.5`} />

      {/* MCG Monogram */}
      <div className={`font-serif font-bold tracking-[0.25em] ${s.monogram} ${primaryColor} leading-none`}>
        MCG
      </div>

      {/* Bottom gold line */}
      <div className={`${s.lineWidth} h-px ${lineColor} mt-1.5 mb-1.5`} />

      {/* Full name */}
      <div className={`font-serif font-medium tracking-[0.3em] ${s.name} ${secondaryColor} uppercase`}>
        MARQUEE COMMODITY GROUP
      </div>

      {/* Optional tagline */}
      {showTagline && (
        <div className={`font-body italic tracking-[0.15em] ${s.tagline} ${accentColor} mt-1`}>
          Independent Transaction Architecture
        </div>
      )}
    </div>
  );
};

/**
 * Horizontal nav logo - MCG monogram + text side by side
 */
const NavLogo = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Left gold accent line */}
      <div className="w-8 h-px bg-gold-400/60 hidden sm:block" />

      {/* MCG monogram */}
      <span className="font-serif font-bold text-3xl md:text-4xl tracking-[0.2em] text-gold-400 leading-none">
        MCG
      </span>

      {/* Separator dot */}
      <div className="w-1.5 h-1.5 rounded-full bg-gold-400/60" />

      {/* Full name */}
      <span className="font-serif font-semibold text-[13px] md:text-[15px] tracking-[0.2em] text-cream-100 leading-tight hidden sm:block">
        MARQUEE<br />COMMODITY GROUP
      </span>

      {/* Right gold accent line */}
      <div className="w-8 h-px bg-gold-400/60 hidden sm:block" />
    </div>
  );
};

export { Logo, NavLogo };
export default Logo;
